import React, { useEffect, useRef } from 'react';
import { DateCarouselItem } from '@/components/DiaryHeader/DateCarouselItem';
import type { DiaryEntry } from '@/types/diary';
import {
  isSameApiDate,
  isSameDay,
  startOfToday,
  toApiDate,
} from '@/utils/date';
import { hasDiaryContent } from '@/utils/diary';

interface Props {
  dates: Date[];
  value: Date;
  entries: DiaryEntry[];
  onChange: (date: Date) => void;
}

export const DateCarousel: React.FC<Props> = ({
  dates,
  value,
  entries,
  onChange,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);
  const hasCentredOnceRef = useRef(false);
  const centredWidthRef = useRef(0);
  const today = startOfToday();

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    const centreSelected = (behavior: ScrollBehavior) => {
      const selected = selectedRef.current;

      if (!selected) {
        return;
      }

      scroller.scrollTo({
        left:
          selected.offsetLeft -
          (scroller.clientWidth - selected.clientWidth) / 2,
        behavior,
      });
      centredWidthRef.current = scroller.clientWidth;
    };

    centreSelected(hasCentredOnceRef.current ? 'smooth' : 'instant');
    hasCentredOnceRef.current = true;

    const observer = new ResizeObserver(() => {
      if (scroller.clientWidth === centredWidthRef.current) {
        return;
      }

      centreSelected('instant');
    });

    observer.observe(scroller);

    return () => observer.disconnect();
  }, [value]);

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="scrollbar-hidden relative flex gap-1 overflow-x-auto px-4 pt-5 pb-5"
      >
        {dates.map((date) => {
          const isSelected = isSameDay(date, value);
          const apiDate = toApiDate(date);
          const entry = entries.find((loadedEntry) =>
            isSameApiDate(loadedEntry.date, apiDate),
          );

          return (
            <div
              key={date.toISOString()}
              ref={isSelected ? selectedRef : undefined}
            >
              <DateCarouselItem
                date={date}
                isSelected={isSelected}
                isToday={isSameDay(date, today)}
                isEmpty={!!entry && !hasDiaryContent(entry)}
                hasChanges={
                  !!entry?.lessons.some((lesson) => lesson.isOnlyForCurrentWeek)
                }
                onClick={() => onChange(date)}
              />
            </div>
          );
        })}
      </div>

      <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-background to-background/0" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background to-background/0" />
    </div>
  );
};
